import {
  IGroupCollapsed,
  IGroupRowCreate,
  ISelectedCellRange,
  ISelectedCellState,
  ITableRowInGroup,
  ITreeGroupRow,
  IViewPortColumn
} from 'components/spreadsheet/types'
import { ITableRow } from 'types'
import { INITIAL_SELECTED_CELL, INITIAL_SELECTED_CELL_RANGE } from 'components/spreadsheet/constants/const'
import { isDataRow } from 'components/spreadsheet/helpers/functions'

/** A row as the grid renders it: a real data row, or one of the group / add-row placeholders. */
type SelectableRow = ITableRow | ITableRowInGroup | IGroupRowCreate | IGroupCollapsed | ITreeGroupRow

/**
 * Resolve the range to use for copy/paste/range-aware operations.
 *
 * When the user has a single cell selected, `selectedCellRange` is the
 * `INITIAL_SELECTED_CELL_RANGE` **singleton** (a module-level constant) and
 * this function synthesises a single-cell range from `selectedCell`.
 *
 * The `===` check against the singleton is load-bearing: callers (e.g. the
 * drag-fill snapshot in `contexts/data/index.tsx`) must avoid spreading the
 * singleton into a fresh object, otherwise this function falls through and
 * returns a range with `endRowIndex: -1`, which makes copy pull every row
 * above the selected cell.
 */
export const getSelectedRange = (
  selectedCell: ISelectedCellState,
  selectedCellRange: ISelectedCellRange
): ISelectedCellRange => {
  if (selectedCell !== INITIAL_SELECTED_CELL && selectedCellRange === INITIAL_SELECTED_CELL_RANGE) {
    return {
      endRowIndex: selectedCell.rowNumber,
      endColumnIndex: selectedCell.columnNumber
    }
  }
  return selectedCellRange
}

/**
 * Build the extended range a fill operation should set after filling down to
 * `targetRowIndex`.
 *
 * When the source was a single cell, `selectedCellRange` has `endColumnIndex:
 * -1` and `endColumnId: undefined`. `inSelectedCellRange` in the cell
 * renderer returns `false` in that case — so without a fallback, the filled
 * cells don't highlight as selected. Fall back to the anchor cell's column
 * so the extended range is well-formed in every case.
 */
export const buildExtendedFillRange = (
  selectedCell: ISelectedCellState,
  selectedCellRange: ISelectedCellRange,
  targetRowIndex: number
): ISelectedCellRange => ({
  endRowIndex: targetRowIndex,
  endColumnIndex:
    selectedCellRange.endColumnIndex !== -1 ? selectedCellRange.endColumnIndex : selectedCell.columnNumber,
  endColumnId: selectedCellRange.endColumnId ?? selectedCell.columnId
})

/**
 * The selection covering every cell in the current view — what Ctrl/Cmd+A sets.
 *
 * The grid is virtualised, so "all cells" can't be read off the rendered DOM: the range is built
 * from the full logical lists instead. `rows` is the rendered row list (already filtered, sorted
 * and grouped for this view) and `columns` the selectable columns in display order, so the
 * rectangle spans exactly the view — never rows a filter removed or columns it hides.
 */
export const buildSelectAllSelection = (
  rows: SelectableRow[],
  columns: IViewPortColumn[]
): { selectedCell: ISelectedCellState; selectedCellRange: ISelectedCellRange } | null => {
  if (columns.length === 0) return null

  const firstRowIndex = rows.findIndex((row) => isDataRow(row))
  if (firstRowIndex === -1) return null

  let lastRowIndex = firstRowIndex
  for (let i = rows.length - 1; i > firstRowIndex; i--) {
    const row = rows[i]
    if (row && isDataRow(row)) {
      lastRowIndex = i
      break
    }
  }

  const firstRow = rows[firstRowIndex]
  const lastColumn = columns[columns.length - 1]

  return {
    selectedCell: {
      rowId: (firstRow as ITableRow).publicId,
      columnId: columns[0].column.publicId,
      rowNumber: firstRowIndex,
      columnNumber: 0,
      editing: false,
      value: ''
    },
    selectedCellRange: {
      endRowIndex: lastRowIndex,
      endColumnIndex: columns.length - 1,
      endColumnId: lastColumn.column.publicId
    }
  }
}
